import { useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  FILTROS_VACIOS, consulta, descargar, useCatalogo, useDatos,
  type Filtros, type InformeDelCatalogo,
} from './api';
import { ControlesDeFiltro, mesDeHoy } from './filtros';
import { CabeceraDeInforme } from './Informes';

/**
 * Personalizado — varios informes en un archivo, y qué columnas lleva cada uno.
 *
 * ## Lo que cambia
 *
 * - **El formato de descarga ya no usa las clases del modal de encuestas.** Las
 *   tomaba prestadas (`.survey-type-*`), y cuando Encuestas pasó a React se
 *   borraron creyendo que no las usaba nadie más: esta pantalla se quedó sin
 *   estilo durante unas horas. Aquí el selector es suyo.
 * - **Las columnas de cada informe se piden una vez y se recuerdan.** El
 *   vanilla las volvía a pedir cada vez que se marcaba y desmarcaba la casilla,
 *   y perdía por el camino lo que ya estuviera elegido.
 * - **Si el informe de encuesta está marcado sin encuesta elegida, lo dice ahí
 *   mismo**, en vez de mandar al final un aviso que pedía volver a marcar.
 */
export function Personalizado() {
  const catalogo = useCatalogo();
  const [filtros, setFiltros] = useState<Filtros>({ ...FILTROS_VACIOS, mes: mesDeHoy() });
  const [elegidos, setElegidos] = useState<string[]>([]);
  // Qué columnas lleva cada informe. Si una clave no está aquí, van todas.
  const [columnas, setColumnas] = useState<Record<string, number[]>>({});
  const [formato, setFormato] = useState<'single' | 'sheets'>('single');
  const [error, setError] = useState<string | null>(null);

  function alternar(clave: string, puesto: boolean) {
    setElegidos((e) => (puesto ? [...e, clave] : e.filter((k) => k !== clave)));
    setError(null);
  }

  function descargarTodo() {
    if (!elegidos.length) { setError('Marca al menos un informe.'); return; }
    if (elegidos.includes('encuesta') && !filtros.survey_id) {
      setError('Elige la encuesta que quieres incluir.'); return;
    }
    for (const clave of elegidos) {
      const suyas = columnas[clave];
      if (suyas && suyas.length === 0) {
        setError('Cada informe marcado tiene que llevar al menos una columna.'); return;
      }
    }
    setError(null);
    const extra: Record<string, string> = {
      sources: elegidos.join(','),
      layout: formato,
    };
    for (const clave of elegidos) {
      const suyas = columnas[clave];
      if (suyas?.length) extra[`cols_${clave}`] = suyas.join(',');
    }
    void descargar(`/reports/custom/export?${consulta(filtros, extra)}`);
  }

  if (catalogo.isPending) {
    return (
      <div aria-busy="true">
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (catalogo.isError || !catalogo.data) {
    return (
      <EstadoError
        err={catalogo.error}
        que="el catálogo de informes"
        onReintentar={() => void catalogo.refetch()}
        onVolver={() => { location.hash = '#informes'; }}
      />
    );
  }

  return (
    <>
      <CabeceraDeInforme icono="bi-sliders2" titulo="Informe personalizado" />

      <Card>
        <div className="flex flex-col gap-5 p-4">
          <p className="text-3 text-muted">
            Marca qué información incluir y con qué columnas. Todo se combina en una hoja;
            abajo puedes pedir una hoja por informe.
          </p>

          <section aria-labelledby="inf-fuentes">
            <h2 id="inf-fuentes" className="mb-2 text-3 font-bold text-brand-ink">
              <i className="bi bi-list-check" aria-hidden="true" /> Qué incluir
            </h2>
            <ul className="flex flex-col gap-2">
              {catalogo.data.reports.map((r) => (
                <li key={r.key}>
                  <Fuente
                    informe={r}
                    puesta={elegidos.includes(r.key)}
                    filtros={filtros}
                    seleccion={columnas[r.key]}
                    onAlternar={(v) => alternar(r.key, v)}
                    onColumnas={(cols) => setColumnas((c) => ({ ...c, [r.key]: cols }))}
                  />
                </li>
              ))}
            </ul>
          </section>

          <section aria-labelledby="inf-filtros">
            <h2 id="inf-filtros" className="mb-2 text-3 font-bold text-brand-ink">
              <i className="bi bi-funnel" aria-hidden="true" /> Filtros
            </h2>
            <div className="flex flex-wrap items-end gap-3">
              <ControlesDeFiltro
                catalogo={catalogo.data}
                pide={['estado', 'area', 'survey']}
                filtros={filtros}
                onCambio={(cambio) => { setFiltros((f) => ({ ...f, ...cambio })); setError(null); }}
              />
            </div>
          </section>

          <FormatoDeDescarga valor={formato} onCambio={setFormato} />

          {error && (
            <p role="alert" className="text-3 font-semibold text-danger-ink">
              <i className="bi bi-exclamation-circle" aria-hidden="true" /> {error}
            </p>
          )}

          <div>
            <Button onClick={descargarTodo}>
              <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Descargar informe
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
}

/** Un informe del catálogo con su casilla y, si está marcado, sus columnas. */
function Fuente({
  informe, puesta, filtros, seleccion, onAlternar, onColumnas,
}: {
  informe: InformeDelCatalogo;
  puesta: boolean;
  filtros: Filtros;
  seleccion: number[] | undefined;
  onAlternar: (v: boolean) => void;
  onColumnas: (cols: number[]) => void;
}) {
  // Sus columnas salen de pedir UNA fila del informe. Solo cuando está marcado,
  // y el de encuesta necesita además que haya una elegida.
  const faltaEncuesta = informe.key === 'encuesta' && !filtros.survey_id;
  const datos = useDatos(informe.key, filtros, puesta && !faltaEncuesta, 1);
  const columnas = datos.data?.columns ?? [];
  const todas = seleccion == null;

  return (
    <div className="rounded-sm border border-line p-3">
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={puesta}
          onChange={(e) => onAlternar(e.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-[var(--color-brand)] focus-visible:focus-anillo"
        />
        <span className="min-w-0">
          <strong className="text-3 text-ink">
            <i className={cn('bi text-brand-ink', informe.icon)} aria-hidden="true" /> {informe.name}
          </strong>
          <span className="mt-0.5 block text-2 text-muted">{informe.desc}</span>
        </span>
      </label>

      {puesta && (
        <div className="mt-2 border-t border-line pt-2">
          {faltaEncuesta ? (
            <p className="text-2 text-warn-ink">
              <i className="bi bi-arrow-down" aria-hidden="true" /> Elige la encuesta en los
              filtros de abajo y sus columnas aparecerán aquí.
            </p>
          ) : datos.isPending ? (
            <Skeleton className="h-5 w-40" />
          ) : datos.isError ? (
            <p className="flex flex-wrap items-center gap-2 text-2 text-danger-ink">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />
              No se pudieron leer sus columnas.
              <Button variant="ghost" size="sm" onClick={() => void datos.refetch()}>Reintentar</Button>
            </p>
          ) : (
            <>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-2 text-muted">Columnas a incluir</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onColumnas(
                    todas || seleccion.length === columnas.length
                      ? []
                      : columnas.map((_, i) => i),
                  )}
                >
                  {todas || seleccion.length === columnas.length
                    ? 'Desmarcar todas'
                    : 'Marcar todas'}
                </Button>
              </div>
              <ul className="grid gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                {columnas.map((c, i) => (
                  <li key={i}>
                    <label className="flex cursor-pointer items-center gap-2 text-2 text-ink-2">
                      <input
                        type="checkbox"
                        checked={todas || seleccion.includes(i)}
                        onChange={(e) => {
                          const actuales = todas ? columnas.map((_, k) => k) : seleccion;
                          onColumnas(e.target.checked
                            ? [...actuales, i].sort((a, b) => a - b)
                            : actuales.filter((k) => k !== i));
                        }}
                        className="size-3.5 shrink-0 accent-[var(--color-brand)] focus-visible:focus-anillo"
                      />
                      {c}
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const FORMATOS = [
  {
    valor: 'single' as const, icono: 'bi-file-earmark-spreadsheet', titulo: 'Una sola hoja',
    texto: 'Todos los informes combinados y apilados en una hoja.',
  },
  {
    valor: 'sheets' as const, icono: 'bi-files', titulo: 'Una hoja por informe',
    texto: 'Cada informe en su propia hoja del mismo archivo.',
  },
];

function FormatoDeDescarga({
  valor, onCambio,
}: {
  valor: 'single' | 'sheets';
  onCambio: (v: 'single' | 'sheets') => void;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-3 font-bold text-brand-ink">
        <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Formato de descarga
      </legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {FORMATOS.map((f) => (
          <label key={f.valor}>
            <input
              type="radio"
              name="formato-de-descarga"
              className="peer sr-only"
              checked={valor === f.valor}
              onChange={() => onCambio(f.valor)}
            />
            <span
              className={cn(
                'flex h-full cursor-pointer items-start gap-2 rounded-sm border border-line bg-surface p-3',
                'hover:border-line-strong peer-focus-visible:focus-anillo',
                'peer-checked:border-brand-ink peer-checked:bg-brand/8',
              )}
            >
              <i className={cn('bi mt-0.5 text-4 text-brand-ink', f.icono)} aria-hidden="true" />
              <span className="min-w-0">
                <strong className="block text-3 text-ink">{f.titulo}</strong>
                <span className="block text-2 text-muted">{f.texto}</span>
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
